// src/components/challenges/ChallengeDashboard.tsx

import React, { useState, useEffect } from "react";
import styled from "styled-components";
import axios from "axios";
import openNotification from "components/utils/CustomNotification";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Challenge,
  UserTimelineData,
  UnifiedLeaderboardEntry,
  ChallengeMappingData,
  UserListItem,
} from "./challenge.types.";
import UserTimelineModal from "./UserTimeLineModal";
import { fetchChallengeMappings } from "./ChallengeUtils/ChallengesApiUtils";
// At the top of ChallengeDashboard.tsx, add this import:
import {
  getChallengeStatus,
  getChallengeStartDate,
  getChallengeEndDate,
  getMetricIcon,
  getGoalText,
  getGoalUnit,
} from "./ChallengeUtils/ChallengeUtils";
import { formatDateRange, formatDate } from "components/utils/DateUtils";
import { getImageFromCache, saveImageToCache } from "../utils/Imagecacheutils";
import { withUIDirectives } from "components/utils/UI_Directive/showUIDirectives";
import ViewChats from "./ViewChats";
import { SessionExtend } from "components/utils/SessionCheckUtils";
import { useCheckSessionHeartBeat } from "components/API/ApiEnpoints";
import { number } from "framer-motion";
import { SlFire } from "react-icons/sl";
import { IoFootsteps } from "react-icons/io5";
import { FaMapMarkerAlt } from "react-icons/fa";

const DashboardContainer = styled.div`
  display: flex;
  flex-direction: column; // Change from row to column
  gap: 24px;
  padding: 24px;
  background-color: var(--page-color);
  min-height: calc(100vh - 80px);

  // Responsive breakpoint - stack vertically on tablets and mobile
  @media (max-width: 1024px) {
    flex-direction: column;
    gap: 20px;
    padding: 16px;
  }

  @media (max-width: 640px) {
    padding: 12px;
    gap: 16px;
  }
`;
const RowContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 24px;
  width: 100%;
  // background-color: var(--card-background);

  @media (max-width: 1024px) {
    flex-direction: column;
    gap: 20px;
  }

  @media (max-width: 640px) {
    gap: 16px;
  }
`;

const MainContent = styled.div`
  flex: 1;
  max-width: 50%;
  display: flex;
  flex-direction: column;
  gap: 24px;
  background-color: var(--page-color);

  // Responsive adjustments
  @media (max-width: 1024px) {
    max-width: 100%;
    flex: none;
  }
`;

const ChallengeCard = styled.div`
  background: white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  height: fit-content;

  @media (max-width: 640px) {
    border-radius: 12px;
  }
`;
const ChallengeImage = styled.div<{ backgroundImage: string }>`
  height: 400px;
  background-image: ${(props) => props.backgroundImage};
  background-size: cover;
  background-position: center;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.2));
  }

  // Responsive height adjustments
  @media (max-width: 1024px) {
    height: 350px;
  }

  @media (max-width: 640px) {
    height: 280px;
  }
`;

const ChallengeInfo = styled.div`
  padding: 24px;
  background-color: var(--card-background);
`;

const ChallengeMeta = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  flex-wrap: wrap;
  gap: 12px;

  @media (max-width: 640px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const DateRange = styled.div`
  color: #6b7280;
  font-size: 14px;
  font-weight: 500;
`;

const ParticipantCount = styled.div`
  background: #f3f4f6;
  color: #4b5563;
  padding: 6px 12px;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 600;
`;

const AboutSection = styled.div`
  margin-top: 16px;
`;

const AboutTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #9ca3af;
  margin-bottom: 8px;
`;

const AboutText = styled.p`
  color: var(--text-color);
  line-height: 1.6;
  margin: 0;
`;

const Sidebar = styled.div`
  width: 50%;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 24px;

  // Responsive adjustments
  @media (max-width: 1024px) {
    width: 100%;
    gap: 20px;
  }

  @media (max-width: 640px) {
    gap: 16px;
  }
`;

const FullWidthSection = styled.div`
  width: 96%;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const LeaderboardCard = styled.div`
  background: var(--card-background);
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  display: flex;
  flex-direction: column;
  flex: 1; // Make it flexible to match height
  max-height: calc(
    363px + 48px + 100px
  ); // ChallengeImage height + ChallengeInfo padding + extra for About section

  @media (max-width: 1024px) {
    max-height: calc(363px + 48px + 100px);
  }

  @media (max-width: 640px) {
    padding: 20px;
    border-radius: 12px;
    max-height: calc(363px + 48px + 100px);
  }
`;

const LeaderboardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 20px;
`;

const TrophyIcon = styled.div`
  color: #f59e0b;
  font-size: 20px;
`;

const LeaderboardTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: var(--text-color);
  margin: 0;
`;

const LeaderboardList = styled.div<{ hasCurrentUser?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 12px;
  overflow-y: auto;
  flex: 1;
  min-height: 0;
  // Dynamically adjust max-height based on whether current user section is shown
  max-height: ${(props) => (props.hasCurrentUser ? "380px" : "100%")};

  // Custom scrollbar styling
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 3px;

    &:hover {
      background: #a8a8a8;
    }
  }
`;

const JoinButton = styled.button`
  padding: 6px 12px;
  background: #00a86b;
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 0.875rem;
  font-weight: bold;
  cursor: pointer;
  transition: background 0.3s;

  &:hover {
    background: #007a52;
  }
`;
const Badge = styled.div`
  background: #28a745;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  color: white;
  font-weight: bold;
`;
const LeaderboardItem = styled.div<{ clickable?: boolean; isTeam?: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 12px;
  border-radius: 12px;
  background: ${(props) =>
    props.isTeam
      ? "linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)"
      : "#f8fafc"};
  cursor: ${(props) => (props.clickable ? "pointer" : "default")};
  transition: all 0.2s ease;
  border: ${(props) => (props.isTeam ? "1px solid #cbd5e1" : "none")};

  &:hover {
    ${(props) =>
      props.clickable &&
      `
      background: ${
        props.isTeam
          ? "linear-gradient(135deg, #e2e8f0 0%, #cbd5e1 100%)"
          : "#e2e8f0"
      };
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    `}
  }

  @media (max-width: 640px) {
    padding: 12px 10px;
    gap: 10px;
  }
`;

const RankBadge = styled.div<{ rank: number }>`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 14px;
  color: white;
  background: ${(props) => {
    if (props.rank === 1) return "linear-gradient(135deg, #f59e0b, #d97706)";
    if (props.rank === 2) return "linear-gradient(135deg, #9ca3af, #6b7280)";
    if (props.rank === 3) return "linear-gradient(135deg, #cd7c2f, #a16207)";
    return "linear-gradient(135deg, #6b7280, #4b5563)";
  }};
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  @media (max-width: 640px) {
    width: 28px;
    height: 28px;
    font-size: 12px;
  }
`;
const UserAvatar = styled.div<{ image?: string; isTeam?: boolean }>`
  width: 40px;
  height: 40px;
  border-radius: ${(props) => (props.isTeam ? "8px" : "50%")};
  background: ${(props) => {
    if (props.image) return `url(${props.image})`;
    if (props.isTeam) return "linear-gradient(135deg, #3b82f6, #1d4ed8)";
    // Default user avatar background - just a gray gradient, no letter
    return "linear-gradient(135deg, #e5e7eb, #d1d5db)";
  }};
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${(props) => (props.isTeam ? "white" : "#6b7280")};
  font-weight: 600;
  font-size: ${(props) => (props.isTeam ? "16px" : "14px")};
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  @media (max-width: 640px) {
    width: 36px;
    height: 36px;
    font-size: ${(props) => (props.isTeam ? "14px" : "12px")};
  }
`;

const UserInfo = styled.div`
  flex: 1;
  min-width: 0; // Allows text truncation
`;
const UserName = styled.div<{ isTeam?: boolean }>`
  font-weight: ${(props) => (props.isTeam ? "600" : "500")};
  color: #1f2937;
  margin-bottom: 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  @media (max-width: 640px) {
    font-size: 14px;
  }
`;

const UserStats = styled.div<{ isTeam?: boolean }>`
  font-size: 13px;
  color: #6b7280;
  display: flex;
  align-items: center;
  gap: 8px;

  @media (max-width: 640px) {
    font-size: 12px;
  }
`;
const TeamMemberCount = styled.span`
  background: #e2e8f0;
  color: #475569;
  padding: 2px 6px;
  border-radius: 8px;
  font-size: 10px;
  font-weight: 600;
`;
const ViewMore = styled.button`
  margin-top: 16px;
  width: 100%;
  padding: 12px;
  background: transparent;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  color: #6b7280;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #f9fafb;
    border-color: #d1d5db;
  }
`;

const TeamPerformanceButton = styled.button`
  width: 100%;
  padding: 16px;
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s ease;
  font-size: 14px;

  &:hover {
    background: #2563eb;
  }

  @media (max-width: 640px) {
    padding: 14px;
    font-size: 13px;
  }
`;

const LoadingSpinner = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px;
  color: #6b7280;
`;

const TopOverlays = styled.div`
  position: absolute;
  top: 20px;
  left: 20px;
  right: 20px;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  z-index: 3;

  @media (max-width: 640px) {
    top: 16px;
    left: 16px;
    right: 16px;
    flex-direction: column;
    gap: 12px;
  }
`;

const GoalBadgeTopLeft = styled.div`
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(10px);
  border-radius: 25px;
  padding: 12px 20px;
  display: flex;
  align-items: center;
  gap: 10px;
  color: white;
  font-weight: 600;
  font-size: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);

  @media (max-width: 640px) {
    padding: 10px 16px;
    font-size: 14px;
    align-self: flex-start;
  }
`;

const GoalIcon = styled.div`
  width: 32px;
  height: 32px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;

  @media (max-width: 640px) {
    width: 28px;
    height: 28px;
    font-size: 14px;
  }
`;

const MembersBadge = styled.div`
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  padding: 8px 16px;
  display: flex;
  align-items: center;
  gap: 8px;
  color: #2d3748;
  font-weight: 600;
  font-size: 14px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);

  @media (max-width: 640px) {
    padding: 6px 12px;
    font-size: 12px;
    align-self: flex-start;
  }
`;
const MembersIcon = styled.div`
  font-size: 16px;
  margin-bottom: 4px;
`;

const BottomOverlay = styled.div`
  position: absolute;
  bottom: 20px;
  left: 20px;
  right: 20px;
  display: flex;
  justify-content: space-between;
  align-items: flex-end; // Both containers align to bottom
  z-index: 3;

  @media (max-width: 640px) {
    bottom: 16px;
    left: 16px;
    right: 16px;
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }
`;
const BottomLeftContainer = styled.div`
  display: flex;
  align-items: flex-end; // Aligns to bottom of this container

  @media (max-width: 640px) {
    order: 1;
  }
`;

const ChallengeDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: flex-end;
  text-align: right;

  @media (max-width: 640px) {
    align-items: flex-start;
    text-align: left;
    order: 2;
  }
`;

const StatusBadge = styled.div<{ status: string }>`
  background: ${(props) => {
    const status = props.status.toLowerCase();
    switch (status) {
      case "live":
        return "rgba(16, 185, 129, 0.9)";
      case "ended":
        return "rgba(107, 114, 128, 0.9)";
      case "yet to start":
        return "rgba(59, 130, 246, 0.9)";
      default:
        return "rgba(107, 114, 128, 0.9)";
    }
  }};
  color: white;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  text-transform: capitalize;
  backdrop-filter: blur(10px);
  align-self: flex-end;
`;

const ChallengeName = styled.h2`
  color: white;
  font-size: 18px;
  font-weight: 700;
  margin: 0;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
  max-width: 250px;
  text-align: right;

  @media (max-width: 1024px) {
    font-size: 16px;
    max-width: 220px;
  }

  @media (max-width: 640px) {
    font-size: 14px;
    text-align: left;
    max-width: 100%;
  }
`;

const ChallengeDateRange = styled.div`
  color: white;
  font-size: 16px;
  font-weight: 500;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
  opacity: 0.9;

  @media (max-width: 1024px) {
    font-size: 15px;
  }

  @media (max-width: 640px) {
    font-size: 14px;
  }
`;

const VisibilityBadge = styled.div<{ visibility: string }>`
  background: ${(props) =>
    props.visibility === "public"
      ? "rgba(255, 255, 255, 0.9)"
      : "rgba(59, 130, 246, 0.9)"};
  color: ${(props) => (props.visibility === "public" ? "#2d3748" : "white")};
  padding: 6px 12px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  text-transform: capitalize;
  backdrop-filter: blur(10px);
  border: ${(props) =>
    props.visibility === "public" ? "1px solid rgba(0, 0, 0, 0.1)" : "none"};

  @media (max-width: 640px) {
    align-self: flex-start;
  }
`;
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(4px);
`;

const QuitModalContainer = styled.div`
  background: white;
  border-radius: 16px;
  padding: 32px;
  max-width: 500px;
  width: 90%;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  animation: slideUp 0.3s ease;

  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const QuitModalHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
`;

const WarningIcon = styled.div`
  width: 48px;
  height: 48px;
  background: #fff3cd;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
`;

const QuitModalTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
`;

const QuitModalMessage = styled.p`
  color: #6b7280;
  line-height: 1.6;
  margin-bottom: 24px;
  font-size: 0.95rem;
`;

const QuitReasonLabel = styled.label`
  display: block;
  font-size: 0.875rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 8px;
`;

const QuitReasonTextarea = styled.textarea`
  width: 100%;
  min-height: 100px;
  padding: 12px;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  font-size: 0.875rem;
  font-family: inherit;
  resize: vertical;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #3b82f6;
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const QuitModalActions = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 24px;
  justify-content: flex-end;
`;

const CancelButton = styled.button`
  padding: 10px 24px;
  background: #f3f4f6;
  color: #374151;
  border: none;
  border-radius: 12px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #e5e7eb;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ConfirmQuitButton = styled.button`
  padding: 10px 24px;
  background: #dc3545;
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 8px;

  &:hover {
    background: #c82333;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(220, 53, 69, 0.3);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const QuitButton = styled.button`
  padding: 8px 20px;
  background: #fff;
  color: #dc3545;
  border: 2px solid #dc3545;
  border-radius: 12px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;

  &:hover {
    background: #dc3545;
    color: white;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(220, 53, 69, 0.3);
  }
`;
const ChatButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
  }

  &:active {
    transform: translateY(0);
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const ChatIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
  </svg>
);

const ChatBadge = styled.span`
  background: rgba(255, 255, 255, 0.3);
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 700;
  margin-left: 4px;
`;

// Add these styled components for rewards section
const RewardsCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  width: 100%;

  @media (max-width: 1024px) {
    width: 98%;
  }
  @media (max-width: 920px) {
    width: 97%;
  }
  @media (max-width: 800px) {
    width: 96%;
  }
  @media (max-width: 640px) {
    padding: 20px;
    border-radius: 12px;
    width: 94%;
  }
  @media (max-width: 420px) {
    padding: 20px;
    border-radius: 12px;
    width: 93%;
  }
  @media (max-width: 320px) {
    padding: 20px;
    border-radius: 12px;
    width: 92%;
  }
`;

const RewardsHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 20px;
`;

const RewardIcon = styled.div`
  color: #f59e0b;
  font-size: 20px;
`;

const RewardsTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: var(--text-color);
  margin: 0;
`;

const RewardsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const RewardCategory = styled.div`
  &:not(:last-child) {
    padding-bottom: 16px;
    border-bottom: 1px solid #e5e7eb;
  }
`;

const CategoryTitle = styled.h4`
  font-size: 14px;
  font-weight: 600;
  color: #4b5563;
  margin-bottom: 12px;
  text-transform: capitalize;
`;

const RewardList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const PositionRewardItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: #fef3c7;
  border-radius: 12px;
  border: 1px solid #fbbf24;
`;

const RangeRewardItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: #dbeafe;
  border-radius: 12px;
  border: 1px solid #60a5fa;
`;

const PositionBadge = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: linear-gradient(135deg, #f59e0b, #d97706);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 14px;
`;

const RangeBadge = styled.div`
  background: linear-gradient(135deg, #3b82f6, #1d4ed8);
  color: white;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  min-width: 80px;
  text-align: center;
`;

const RewardContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const RewardItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: white;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
`;

const RewardTypeIcon = styled.div<{ type: string }>`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: ${(props) => {
    switch (props.type) {
      case "points":
        return "#10b981";
      case "voucher":
        return "#8b5cf6";
      case "coupon":
        return "#f59e0b";
      default:
        return "#6b7280";
    }
  }};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: bold;
`;

const RewardInfo = styled.div`
  flex: 1;
`;

const RewardName = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: #1f2937;
  margin-bottom: 2px;
`;

const RewardDetails = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: #6b7280;
`;

const RewardAmount = styled.span<{ type: string }>`
  font-weight: 600;
  color: ${(props) => {
    switch (props.type) {
      case "points":
        return "#10b981";
      case "voucher":
        return "#8b5cf6";
      case "coupon":
        return "#f59e0b";
      default:
        return "#1f2937";
    }
  }};
`;

const PlatformBadge = styled.span`
  background: #e5e7eb;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 11px;
  color: #4b5563;
`;

const ParticipationRewardSection = styled.div`
  padding: 16px;
  background: linear-gradient(135deg, #f0fdf4, #dcfce7);
  border-radius: 12px;
  border: 1px solid #86efac;
  margin-top: 16px;
`;

const ParticipationHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
`;

const ParticipationIcon = styled.div`
  color: #16a34a;
  font-size: 18px;
`;

const ParticipationTitle = styled.h4`
  font-size: 14px;
  font-weight: 600;
  color: #166534;
  margin: 0;
`;

const ParticipationAmount = styled.div`
  font-size: 24px;
  font-weight: 700;
  color: #16a34a;
  text-align: center;
  margin: 8px 0;
`;

const ParticipationSubtitle = styled.div`
  font-size: 12px;
  color: #166534;
  text-align: center;
  opacity: 0.8;
`;

const NoRewards = styled.div`
  text-align: center;
  padding: 32px 16px;
  color: #6b7280;
  font-size: 14px;
`;

const LoadingSpinnerSmall = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  color: #6b7280;
  font-size: 14px;
`;

const SimplifiedRewardsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const SimplifiedRewardItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  background: #fef3c7;
  border-radius: 12px;
  border: 1px solid #fbbf24;
  // height: 100%;
  min-height: 120px;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(251, 191, 36, 0.2);
  }
`;
const PositionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
`;
const PositionTitle = styled.div<{ isRange?: boolean }>`
  font-size: ${({ isRange }) => (isRange ? "13px" : "14px")};
  font-weight: 600;
  color: #4b5563;
  flex: 1;
  word-break: break-word;
`;

const SimplifiedPositionBadge = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: linear-gradient(135deg, #f59e0b, #d97706);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 14px;
`;

const SimplifiedRewardContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const SimplifiedRewardList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1;
`;

const SimplifiedRewardBadge = styled.div<{ type: string }>`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 10px;
  background: white;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  font-size: 12px;
  font-weight: 500;
  color: ${(props) => {
    switch (props.type) {
      case "points":
        return "#10b981";
      case "voucher":
        return "#8b5cf6";
      case "coupon":
        return "#f59e0b";
      default:
        return "#1f2937";
    }
  }};
  width: 95%;
  min-height: 36px;
  word-break: break-word;
  margin-right: 6px;
`;

const SimplifiedRewardIcon = styled.span`
  font-size: 14px;
  flex-shrink: 0;
`;

const SimplifiedParticipationRewards = styled.div`
  padding: 20px;
  background: linear-gradient(135deg, #f0fdf4, #dcfce7);
  border-radius: 12px;
  border: 1px solid #86efac;
  margin-top: 16px;
  grid-column: 1 / -1;
`;
const RewardText = styled.div`
  flex: 1;
  word-break: break-word;
  overflow-wrap: break-word;
  line-height: 1.4;
`;

const SimplifiedParticipationHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
`;

const SimplifiedParticipationIcon = styled.div`
  color: #16a34a;
  font-size: 18px;
`;

const SimplifiedParticipationTitle = styled.h4`
  font-size: 14px;
  font-weight: 600;
  color: #166534;
  margin: 0;
`;

const SimplifiedParticipationPoints = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  font-weight: 700;
  color: #16a34a;
`;

const SimplifiedParticipationBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: white;
  border-radius: 8px;
  border: 1px solid #86efac;
  font-size: 14px;
  font-weight: 600;
  color: #16a34a;
`;

const CurrentUserPosition = styled.div`
  margin-top: 16px;
  padding-top: 16px;
  // border-top: 1px solid #e5e7eb;
`;

const CurrentUserHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
`;

const UserPositionIcon = styled.div`
  color: #3b82f6;
  font-size: 18px;
`;

const CurrentUserTitle = styled.h4`
  font-size: 14px;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
`;

const CurrentUserItem = styled.div<{ isCurrentUser?: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 12px;
  border-radius: 12px;
  background: ${(props) =>
    props.isCurrentUser
      ? "linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)"
      : "#f8fafc"};
  cursor: pointer;
  transition: all 0.2s ease;
  border: ${(props) => (props.isCurrentUser ? "1px solid #93c5fd" : "none")};

  &:hover {
    background: ${(props) =>
      props.isCurrentUser
        ? "linear-gradient(135deg, #bfdbfe 0%, #93c5fd 100%)"
        : "#e2e8f0"};
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }

  @media (max-width: 640px) {
    padding: 12px 10px;
    gap: 10px;
  }
`;

const CurrentUserBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: #3b82f6;
  color: white;
  padding: 2px 8px;
  border-radius: 8px;
  font-size: 10px;
  font-weight: 600;
  margin-left: 8px;
`;

const DurationInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const InfoLabel = styled.div`
  color: #9ca3af;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const InfoValue = styled.div`
  color: var(--text-color);
  font-size: 16px;
  font-weight: 600;
`;

const ChallengeTypeInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: flex-end;
  text-align: right;

  @media (max-width: 640px) {
    align-items: flex-start;
    text-align: left;
  }
`;
// Define types for rewards
interface Reward {
  reward_type: "points" | "voucher" | "coupon";
  reward_amount: number;
  reward_name?: string;
  reward_uid?: string;
  platform?: string;
  currency?: string;
  expiry_datetime?: string;
}

interface PositionReward {
  distribution_type: "position";
  value: number;
  rewards: Reward[];
}

interface RangeReward {
  distribution_type: "range";
  value: {
    range_min: number;
    range_max: number;
  };
  rewards: Reward[];
}

type RewardDistribution = PositionReward | RangeReward;

interface ChallengeWithRewards extends Challenge {
  has_rewards?: boolean;
  reward_distribution?: RewardDistribution[] | string;
  reward_distribution_datetime?: string;
  total_prize_pool_amount?: string | number;
  has_participation_point?: boolean;
  is_experience_point?: boolean;
  participation_point?: number;
}

interface ChallengeDashboardProps {
  challenge?: Challenge;
}

export const ChallangeIcon = (challange: string) => {
  switch (challange) {
    case "steps":
      return <IoFootsteps />;
    case "distance":
      return <FaMapMarkerAlt />;
    case "caloriesburned":
      return <SlFire />;
    default:
      return null;
  }
};

export const ChallengeText = (challange: string, evaluationMode?: string) => {
  // Base text based on challenge type
  let baseText = "";
  switch (challange) {
    case "steps":
      baseText = "Steps";
      break;
    case "distance":
      baseText = "meters";
      break;
    case "caloriesburned":
      baseText = "kcal";
      break;
    default:
      return null;
  }
  // Add evaluation mode if provided
  if (evaluationMode) {
    if (evaluationMode === "daily") {
      return `${baseText} per day`;
    } else if (evaluationMode === "overall") {
      return baseText;
    }
  }

  return baseText;
};

export const ChallengeLeaderBoardunit = (challange: string) => {
  // Base text based on challenge type
  let baseText = "";
  switch (challange) {
    case "steps":
      baseText = "Steps";
      break;
    case "distance":
      baseText = "meters";
      break;
    case "caloriesburned":
      baseText = "kcal";
      break;
    default:
      return null;
  }
  return baseText;
};

const ChallengeDashboard: React.FC<ChallengeDashboardProps> = ({
  challenge: propChallenge,
}) => {
  useCheckSessionHeartBeat();
  const location = useLocation();
  const navigate = useNavigate();
  const [challenge, setChallenge] = useState<ChallengeWithRewards | null>(null);
  const [leaderboard, setLeaderboard] = useState<UnifiedLeaderboardEntry[]>([]);
  const [challengeMappingData, setChallengeMapping] = useState<
    ChallengeMappingData[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserTimelineData | null>(
    null,
  );
  const [showUserModal, setShowUserModal] = useState(false);
  const sessionUid = localStorage.getItem("session_uid") || "";
  const userUid = localStorage.getItem("user_uid") || "";
  const [showQuitModal, setShowQuitModal] = useState(false);
  const [quitReason, setQuitReason] = useState("");
  const [isQuitting, setIsQuitting] = useState(false);
  const [showChats, setShowChats] = useState(false);

  // Add state for rewards
  const [rewardsData, setRewardsData] = useState<RewardDistribution[]>([]);
  const [participationPoints, setParticipationPoints] = useState<number | null>(
    null,
  );
  const [loadingRewards, setLoadingRewards] = useState(false);
  const [hasRewards, setHasRewards] = useState(false);
  const [durationdays, setDurationdays] = useState<number | null>(null);
  // Add state for user images
  const [userImages, setUserImages] = useState<Record<string, string>>({});
  const [showAllLeaderboard, setShowAllLeaderboard] = useState(false);

  useEffect(() => {
    const loadChallengeData = async () => {
      let challengeData: any = null;

      if (propChallenge) {
        challengeData = propChallenge;
        console.log("📦 Challenge from props:", {
          hasImage: !!challengeData.image,
          hasCachedImageUrl: !!challengeData.cachedImageUrl,
          imageDetails: challengeData.image_details,
        });
      }

      // 2️⃣ Try from navigation state
      else if (location.state && typeof location.state === "object") {
        challengeData = location.state;
        console.log("🚀 Challenge from state:", {
          hasImage: !!challengeData.image,
          hasCachedImageUrl: !!challengeData.cachedImageUrl,
          imageDetails: challengeData.image_details,
        });
      }

      // 3️⃣ Try from localStorage (for page refresh)
      else {
        const storedChallenge = localStorage.getItem("selectedChallenge");
        if (storedChallenge) {
          try {
            challengeData = JSON.parse(storedChallenge);
          } catch (error) {
            console.error("❌ Error parsing stored challenge:", error);
          }
        } else {
          console.warn("❌ No stored challenge found in localStorage");
        }
      }

      if (challengeData && typeof challengeData === "object") {
        try {
          // ✅ Extract image_uid from image_details object (API response structure)
          const imageUid =
            challengeData.image_details?.image_uid || challengeData.image_uid;
          const imageTimestamp =
            challengeData.image_details?.timestamp ||
            challengeData.image_timestamp ||
            "";
          const isDefaultImage = challengeData.image_details?.default || false;

          console.log("🖼️ Image details:", {
            imageUid,
            imageTimestamp,
            isDefaultImage,
          });

          // Skip fetching if it's a default image
          if (isDefaultImage) {
            console.log("⏭️ Skipping default image fetch");
            challengeData.image = "";
            setChallenge(challengeData);
            return;
          }

          // Priority 1: Use cachedImageUrl if already available (from ChallengesPage)
          if (
            challengeData.cachedImageUrl &&
            challengeData.cachedImageUrl.startsWith("blob:")
          ) {
            console.log("✅ Using cached blob URL");
            challengeData.image = challengeData.cachedImageUrl;
          }
          // Priority 2: Use existing image if present and valid
          else if (
            challengeData.image &&
            (challengeData.image.startsWith("data:") ||
              challengeData.image.startsWith("blob:"))
          ) {
            console.log("✅ Using existing image data");
          }
          // Priority 3: Try to load from cache
          else if (imageUid) {
            const cachedUrl = await getImageFromCache(imageUid, imageTimestamp);

            if (cachedUrl) {
              console.log("✅ Loaded image from cache");
              challengeData.image = cachedUrl;
            }
            // Priority 4: Fetch from API as last resort
            else {
              console.log(
                "📥 Cache miss - Fetching challenge image from API...",
              );
              const url = new URL(
                `${process.env.REACT_APP_API_URL}/api/get-image-by-uid`,
              );
              url.searchParams.append("image_uid_list", imageUid);
              url.searchParams.set("is_icon_img", "false");
              url.searchParams.set("is_feedback_image", "false");

              const res = await fetch(url.toString(), {
                headers: {
                  "user-uid": userUid,
                },
                credentials: "include",
              });

              const data = await res.json();

              if (
                data.status === "Success" &&
                data.data?.length > 0 &&
                data.data[0].image
              ) {
                const base64Image = data.data[0].image;
                const apiTimestamp =
                  data.data[0].timestamp || data.data[0].timstamp || "";

                console.log("✅ Image fetched from API successfully");

                // Save to cache for next time
                await saveImageToCache(
                  imageUid,
                  imageUid,
                  base64Image,
                  apiTimestamp,
                );

                // Get cached URL or use base64 directly
                const newCachedUrl = await getImageFromCache(
                  imageUid,
                  apiTimestamp,
                );
                challengeData.image =
                  newCachedUrl || `data:image/jpeg;base64,${base64Image}`;
                console.log("✅ Image cached and ready to display");
              } else {
                console.warn("❌ No image found in API response");
                challengeData.image = "";
              }
            }
          } else {
            console.warn("❌ No image_uid available in challenge data");
            challengeData.image = "";
          }
          SessionExtend();
        } catch (err) {
          console.error("❌ Error loading challenge image:", err);
          challengeData.image = "";
        }

        console.log("✅ Final challenge data ready:", {
          name: challengeData.challenge_name,
          hasImage: !!challengeData.image,
          imageType: challengeData.image?.startsWith("blob:")
            ? "blob"
            : challengeData.image?.startsWith("data:")
              ? "base64"
              : "none",
          imageLength: challengeData.image?.length || 0,
        });

        setChallenge(challengeData);
      } else {
        console.error(" No valid challenge data found from any source");
      }
    };

    loadChallengeData();

    // 🧹 Cleanup blob URL on unmount
    return () => {
      if (challenge?.image && challenge.image.startsWith("blob:")) {
        URL.revokeObjectURL(challenge.image);
      }
    };
  }, [propChallenge, location.state]);

  useEffect(() => {
    if (challenge) {
      fetchLeaderboard();
      fetchTeamPerformance();
      // Fetch rewards for this specific challenge
      fetchRewardsForChallenge(challenge.challenge_uid);
    }
  }, [challenge]);

  const fetchRewardsForChallenge = async (challengeUid: string) => {
    setLoadingRewards(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/challenges`,
        {
          headers: {
            accept: "application/json",
            "user-uid": userUid,
          },
          params: {
            challenge_uid: challengeUid, // Add this parameter
          },
          withCredentials: true,
        },
      );

      if (response.data?.status === "Success" && response.data.data) {
        // Find the specific challenge by UID
        const challengeData = response.data.data.find(
          (c: any) => c.challenge_uid === challengeUid,
        );

        if (challengeData) {
          // Update hasRewards state
          setHasRewards(challengeData.has_rewards || false);
          setDurationdays(challengeData.challenge_duration);
          // Process rewards
          if (challengeData.has_rewards && challengeData.reward_distribution) {
            let rewardDistribution = challengeData.reward_distribution;

            // Parse if it's a string
            if (typeof rewardDistribution === "string") {
              try {
                rewardDistribution = JSON.parse(rewardDistribution);
              } catch (e) {
                console.error("Error parsing reward distribution:", e);
                rewardDistribution = [];
              }
            }
            setRewardsData(rewardDistribution);
          } else {
            setRewardsData([]);
          }

          // Process participation points - check if participation_point exists and is not null/undefined
          if (
            challengeData.has_participation_point !== undefined &&
            challengeData.participation_point !== undefined &&
            challengeData.participation_point !== null
          ) {
            console.log(
              "Participation points found:",
              challengeData.participation_point,
            );
            setParticipationPoints(challengeData.participation_point);
          } else {
            console.log("No participation points found");
            setParticipationPoints(null);
          }
        } else {
          // Challenge not found in the list
          setHasRewards(false);
          setRewardsData([]);
          setParticipationPoints(null);
        }
      } else {
        setHasRewards(false);
        setRewardsData([]);
        setParticipationPoints(null);
      }
      SessionExtend();
    } catch (error) {
      console.error("Error fetching rewards from API:", error);
      setHasRewards(false);
      setRewardsData([]);
      setParticipationPoints(null);
    } finally {
      setLoadingRewards(false);
    }
  };

  // Function to fetch user images in batches
  // const fetchUserImages = async (imageUids: string[]) => {
  //   if (imageUids.length === 0) return;

  //   try {
  //     const url = new URL(
  //       `${process.env.REACT_APP_API_URL}/api/get-image-by-uid`
  //     );

  //     // Add all image UIDs to the URL
  //     imageUids.forEach((uid) =>
  //       url.searchParams.append("image_uid_list", uid)
  //     );
  //     url.searchParams.set("is_icon_img", "true");
  //     url.searchParams.set("is_feedback_image", "false");

  //     const res = await fetch(url.toString(), {
  //       headers: {
  //         "session-uid": sessionUid,
  //         "user-uid": userUid,
  //       },
  //     });

  //     const data = await res.json();

  //     if (data.status === "Success" && Array.isArray(data.data)) {
  //       const imageMap: Record<string, string> = {};
  //       data.data.forEach((item: any) => {
  //         if (item.image_uid && item.image && !item.default) {
  //           imageMap[item.image_uid] = item.image;
  //         }
  //       });
  //       setUserImages((prevImages) => ({ ...prevImages, ...imageMap }));
  //     }
  //   } catch (error) {
  //     console.error("Error fetching user images:", error);
  //   }
  // };

  // Function to fetch user images in batches of 100
  const fetchUserImages = async (imageUids: string[]) => {
    if (imageUids.length === 0) return;

    const BATCH_SIZE = 100;
    const batches: string[][] = [];

    // Split imageUids into batches of 100
    for (let i = 0; i < imageUids.length; i += BATCH_SIZE) {
      batches.push(imageUids.slice(i, i + BATCH_SIZE));
    }

    console.log(
      `Fetching ${imageUids.length} images in ${batches.length} batch(es)`,
    );

    // Process each batch
    for (let batchIndex = 0; batchIndex < batches.length; batchIndex++) {
      const batch = batches[batchIndex];

      try {
        const url = new URL(
          `${process.env.REACT_APP_API_URL}/api/get-image-by-uid`,
        );

        // Add batch of image UIDs to the URL
        batch.forEach((uid) => url.searchParams.append("image_uid_list", uid));
        url.searchParams.set("is_icon_img", "true");
        url.searchParams.set("is_feedback_image", "false");

        console.log(
          `Fetching batch ${batchIndex + 1}/${batches.length} with ${
            batch.length
          } images`,
        );

        const res = await fetch(url.toString(), {
          headers: {
            "user-uid": userUid,
          },
          credentials: "include",
        });
        // const res = await fetch(url.toString(), {
        //   headers: {
        //     "session-uid": sessionUid,
        //     "user-uid": userUid,
        //   },
        // });

        const data = await res.json();

        if (data.status === "Success" && Array.isArray(data.data)) {
          const imageMap: Record<string, string> = {};
          data.data.forEach((item: any) => {
            if (item.image_uid && item.image && !item.default) {
              imageMap[item.image_uid] = item.image;
            }
          });

          // Update state with images from this batch
          setUserImages((prevImages) => ({ ...prevImages, ...imageMap }));
          console.log(
            `Successfully fetched ${
              Object.keys(imageMap).length
            } images from batch ${batchIndex + 1}`,
          );
        }
        SessionExtend();
      } catch (error) {
        console.error(
          `Error fetching user images for batch ${batchIndex + 1}:`,
          error,
        );
        // Continue with next batch even if this one fails
      }
    }
  };

  const fetchLeaderboard = async () => {
    if (!challenge) return;

    setLoading(true);
    try {
      const data = await fetchChallengeMappings(
        challenge.challenge_uid,
        userUid,
        sessionUid,
      );

      if (data.status === "Success" && data.data && data.data.length > 0) {
        setChallengeMapping(data.data);

        // Fix 2: Use correct type for leaderboard entries
        const leaderboardEntries: UnifiedLeaderboardEntry[] = [];

        if (challenge.challenge_type === "individual") {
          // For individual challenges, show users sorted by metrics
          const allUsers: UserListItem[] = [];

          data.data.forEach((mapping: ChallengeMappingData) => {
            allUsers.push(...mapping.user_list);
          });

          // Sort users by metrics (descending)
          const sortedUsers = allUsers.sort(
            (a, b) =>
              (b.user_progress_total_value || 0) -
              (a.user_progress_total_value || 0),
          );

          // Collect all image UIDs that need to be fetched
          const imageUidsToFetch: string[] = [];

          sortedUsers.forEach((user, index) => {
            const imageUid = user.image_details?.image_uid;
            if (imageUid && !user.image_details?.default) {
              imageUidsToFetch.push(imageUid);
            }

            leaderboardEntries.push({
              id: user.user_uid,
              name: user.user_name,
              metrics: user.user_progress_total_value || 0,
              rank: index + 1,
              type: "user",
              icon_image: imageUid || undefined,
              user_uid: user.user_uid,
            });
          });

          // Fetch all images in one batch
          if (imageUidsToFetch.length > 0) {
            await fetchUserImages(imageUidsToFetch);
          }
        } else if (challenge.challenge_type === "team") {
          // For team challenges, show teams with aggregated metrics
          data.data.forEach((mapping: ChallengeMappingData, index: any) => {
            const teamMetrics = mapping.user_list.reduce(
              (sum, user) => sum + (user.user_progress_total_value || 0),
              0,
            );

            leaderboardEntries.push({
              id: mapping.group_uid || mapping.challenge_uid,
              name: mapping.group_name || `Team ${index + 1}`,
              metrics: teamMetrics,
              rank: index + 1, // API already returns teams in sorted order
              type: "team",
              group_name: mapping.group_name,
              member_count: mapping.user_count || mapping.user_list.length,
              group_uid: mapping.group_uid,
            });
          });
        }

        setLeaderboard(leaderboardEntries);
      }
      SessionExtend();
    } catch (error) {
      console.error("Error fetching challenge mapping:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTeamPerformance = async () => {
    if (!challenge) return;

    try {
      // Get challenge start and end dates
      const startDate = getChallengeStartDate(challenge);
      const endDate = getChallengeEndDate(challenge);

      // Convert dates to UTC format
      const fromDateTime = new Date(startDate).toISOString();
      const toDateTime = new Date(endDate).toISOString();

      // Build query parameters
      const queryParams = new URLSearchParams({
        from_datetime: fromDateTime,
        to_datetime: toDateTime,
        challenge_uid: challenge.challenge_uid,
        group_uid: "null",
      });

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/team-performance?${queryParams}`,
        {
          headers: {
            accept: "application/json",
            "user-uid": userUid,
          },
          credentials: "include",
        },
      );

      const data = await response.json();

      if (data.status === "Success") {
        console.log("Team performance data:", data.data);
        // Handle the team performance data as needed
        // You might want to store this in state if you need to display it
      }
    } catch (error) {
      console.error("Error fetching team performance:", error);
    }
  };

  // Fix 3: Complete handleUserClick function with proper user timeline logic
  const handleUserClick = async (entry: UnifiedLeaderboardEntry) => {
    console.log("Clicked entry:", entry);

    if (entry.type === "team") {
      navigate("/Challenges-team_performance", {
        state: {
          challenge,
          selectedTeam: entry,
          teamData: challengeMappingData.find(
            (data) => data.group_uid === entry.group_uid,
          ),
          challengeGoal: challenge?.goal_count,
          challengeFrequency: challenge?.goal_evaluation_mode,
        },
      });
      return;
    }

    // For individual users, create user timeline data
    const userData = challengeMappingData
      .flatMap((mapping) => mapping.user_list)
      .find((user) => user.user_uid === entry.user_uid);

    if (userData) {
      // IMPORTANT: Use the correct property name - check your API response
      // It might be challenge_progress, user_progress_map, or progress_data
      const progressData =
        userData.challenge_progress ||
        (userData as any).user_progress_map ||
        (userData as any).progress_data ||
        {};

      // Create daily progress array similar to ChallengesTeamPerformance
      const dailyProgress = Object.entries(progressData)
        .map(([date, progress]: [string, any]) => {
          // Format date
          const formattedDate = new Date(date).toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
          });

          // Extract value and status
          let value = 0;
          let status: "completed" | "incomplete" = "incomplete";

          if (typeof progress === "object" && progress !== null) {
            // Object format: { value: number, status: "completed" | "incomplete" }
            value = progress.value || progress.amount || 0;
            status =
              progress.status === "completed" ? "completed" : "incomplete";
          } else if (typeof progress === "number") {
            // Number format: just the value
            value = progress;
            status =
              value >= (challenge?.goal_count || 0)
                ? "completed"
                : "incomplete";
          }

          return {
            date: formattedDate,
            value: value,
            status: status,
          };
        })
        .sort((a, b) => {
          // Sort by date descending (most recent first)
          const dateA = new Date(a.date.split(" ").reverse().join("-"));
          const dateB = new Date(b.date.split(" ").reverse().join("-"));
          return dateB.getTime() - dateA.getTime();
        });

      // Calculate active days (days with non-zero progress)
      const activeDays = dailyProgress.filter((day) => day.value > 0).length;

      // Get image URL - check if we already have it in userImages state
      const imageUid = userData.image_details?.image_uid;
      let imageUrl = "";

      if (imageUid && !userData.image_details?.default) {
        // First, check if we already have this image in our userImages state
        if (userImages[imageUid]) {
          imageUrl = `data:image/png;base64,${userImages[imageUid]}`;
        } else {
          // If not in state, try to fetch it from the API
          try {
            const url = new URL(
              `${process.env.REACT_APP_API_URL}/api/get-image-by-uid`,
            );
            url.searchParams.append("image_uid_list", imageUid);
            url.searchParams.set("is_icon_img", "true");
            url.searchParams.set("is_feedback_image", "false");

            const res = await fetch(url.toString(), {
              headers: {
                "user-uid": userUid,
              },
              credentials: "include",
            });

            const data = await res.json();

            if (
              data.status === "Success" &&
              Array.isArray(data.data) &&
              data.data.length > 0 &&
              data.data[0].image
            ) {
              const base64Image = data.data[0].image;
              imageUrl = `data:image/png;base64,${base64Image}`;

              // Update the userImages state so we have it for next time
              setUserImages((prev) => ({ ...prev, [imageUid]: base64Image }));
            }
          } catch (error) {
            console.error("Error fetching user image for modal:", error);
          }
        }
      }

      // Create the UserTimelineData object
      const userTimelineData: UserTimelineData = {
        user_uid: userData.user_uid,
        user_name: userData.user_name,
        total_steps: userData.user_progress_total_value || entry.metrics || 0,
        active_days: activeDays,
        daily_progress: dailyProgress,
        image_uid: imageUid,
        image_url: imageUrl, // Add the actual image URL
      };

      setSelectedUser(userTimelineData);
      setShowUserModal(true);
      SessionExtend();
    } else {
      console.error("User data not found for entry:", entry);
      console.error(
        "Available users:",
        challengeMappingData.flatMap((m) => m.user_list),
      );
    }
  };

  const handleTeamPerformanceClick = () => {
    navigate("/challenges/team-performance", { state: { challenge } });
    SessionExtend();
  };

  const handleJoinChallenge = async (challenge: Challenge) => {
    const payload = {
      challenge_uid: challenge.challenge_uid,
      user_uid: userUid,
      org_uid: challenge.org_uid,
    };

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/join-challenge`,
        payload,
        {
          headers: {
            accept: "application/json",
            "user-uid": userUid,
          },
          withCredentials: true,
        },
      );

      if (response.data?.status === "Success") {
        openNotification({
          type: "success",
          message: "Successfully joined challenge!",
        });

        // Refetch the complete challenge data including member count
        await fetchCompleteChallengeData(challenge.challenge_uid);

        // Refetch leaderboard
        await fetchLeaderboard();
      } else {
        openNotification({
          type: "error",
          message: response.data?.data || "Failed to join challenge.",
        });
      }
      SessionExtend();
    } catch (err) {
      openNotification({
        type: "error",
        message: "An error occurred while joining the challenge.",
      });
      console.error("Join API Error:", err);
    }
  };

  const handleQuitChallenge = async () => {
    if (!challenge || !quitReason.trim()) {
      openNotification({
        type: "warning",
        message: "Please provide a reason for quitting.",
      });
      return;
    }

    setIsQuitting(true);

    const payload = {
      challenge_uid: challenge.challenge_uid,
      org_uid: challenge.org_uid,
      user_uid: userUid,
      start_datetime: getChallengeStartDate(challenge),
      reason: quitReason.trim(),
    };

    try {
      const response = await axios.delete(
        `${process.env.REACT_APP_API_URL}/api/quit-challenge`,
        {
          headers: {
            accept: "application/json",
            "user-uid": userUid,
          },
          withCredentials: true,
          data: payload,
        },
      );

      if (response.data?.status === "Success") {
        openNotification({
          type: "success",
          message: "Successfully quit the challenge.",
        });

        // Close modal and reset
        setShowQuitModal(false);
        setQuitReason("");
        window.location.reload();

        // Refetch the complete challenge data including member count
        await fetchCompleteChallengeData(challenge.challenge_uid);

        // Refetch leaderboard
        await fetchLeaderboard();
      } else {
        openNotification({
          type: "error",
          message: response.data?.data || "Failed to quit challenge.",
        });
      }
      SessionExtend();
    } catch (err) {
      openNotification({
        type: "error",
        message: "An error occurred while quitting the challenge.",
      });
      console.error("Quit API Error:", err);
    } finally {
      setIsQuitting(false);
    }
  };

  // Add a new function to fetch complete challenge data
  const fetchCompleteChallengeData = async (challengeUid: string) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/challenges`,
        {
          headers: {
            accept: "application/json",
            "user-uid": userUid,
          },
          params: { challenge_uid: challengeUid },
          withCredentials: true,
        },
      );

      if (response.data?.status === "Success" && response.data.data) {
        // Find the specific challenge by UID
        const challengeData = response.data.data.find(
          (c: any) => c.challenge_uid === challengeUid,
        );

        if (challengeData) {
          const membersJoined =
            challengeData.members_joined ||
            challengeData.member_count ||
            challengeData.participants_count ||
            challengeData.total_participants ||
            challengeData.users_count ||
            0; // Default to 0 if not found

          console.log("Members count from API:", {
            members_joined: challengeData.members_joined,
            member_count: challengeData.member_count,
            participants_count: challengeData.participants_count,
            total_participants: challengeData.total_participants,
            finalValue: membersJoined,
          });
          // Process image data similar to your initial load
          const imageUid =
            challengeData.image_details?.image_uid || challengeData.image_uid;
          const imageTimestamp =
            challengeData.image_details?.timestamp ||
            challengeData.image_timestamp ||
            "";
          const isDefaultImage = challengeData.image_details?.default || false;

          if (isDefaultImage) {
            challengeData.image = "";
          } else if (imageUid) {
            // Try to load from cache first
            const cachedUrl = await getImageFromCache(imageUid, imageTimestamp);

            if (cachedUrl) {
              challengeData.image = cachedUrl;
            } else {
              // Fetch from API
              const url = new URL(
                `${process.env.REACT_APP_API_URL}/api/get-image-by-uid`,
              );
              url.searchParams.append("image_uid_list", imageUid);
              url.searchParams.set("is_icon_img", "false");
              url.searchParams.set("is_feedback_image", "false");

              const res = await fetch(url.toString(), {
                headers: {
                  "user-uid": userUid,
                },
                credentials: "include",
              });

              const data = await res.json();

              if (
                data.status === "Success" &&
                data.data?.length > 0 &&
                data.data[0].image
              ) {
                const base64Image = data.data[0].image;
                const apiTimestamp =
                  data.data[0].timestamp || data.data[0].timstamp || "";

                await saveImageToCache(
                  imageUid,
                  imageUid,
                  base64Image,
                  apiTimestamp,
                );

                const newCachedUrl = await getImageFromCache(
                  imageUid,
                  apiTimestamp,
                );
                challengeData.image =
                  newCachedUrl || `data:image/jpeg;base64,${base64Image}`;
              } else {
                challengeData.image = "";
              }
            }
          } else {
            challengeData.image = "";
          }

          // Update the challenge state with fresh data including updated member count
          setChallenge(challengeData);

          // Also fetch rewards for this challenge
          await fetchRewardsForChallenge(challengeUid);
        }
      }
      SessionExtend();
    } catch (error) {
      console.error("Error fetching updated challenge data:", error);
    }
  };

  const isUserJoined = () => {
    if (!challenge) return false;

    // First check the local challenge state (it should now be updated)
    if (challenge.joined !== undefined) {
      return challenge.joined;
    }

    // Fallback to checking challenge mapping
    if (challengeMappingData && challengeMappingData.length > 0) {
      const userExists = challengeMappingData.some((mapping) =>
        mapping.user_list.some((user) => user.user_uid === userUid),
      );
      return userExists;
    }

    return false;
  };

  useEffect(() => {
    if (challenge && challengeMappingData && challengeMappingData.length > 0) {
      const userExists = challengeMappingData.some((mapping) =>
        mapping.user_list.some((user) => user.user_uid === userUid),
      );

      // Update the challenge joined status if it's different
      if (challenge.joined !== userExists) {
        setChallenge((prev) => (prev ? { ...prev, joined: userExists } : null));
      }
    }
  }, [challengeMappingData, userUid, challenge]);

  // Helper function to group rewards by type
  const groupRewardsByType = () => {
    const positionRewards: PositionReward[] = [];
    const rangeRewards: RangeReward[] = [];

    rewardsData.forEach((reward) => {
      if (reward.distribution_type === "position") {
        positionRewards.push(reward as PositionReward);
      } else if (reward.distribution_type === "range") {
        rangeRewards.push(reward as RangeReward);
      }
    });

    // Sort position rewards by position (ascending)
    positionRewards.sort((a, b) => a.value - b.value);

    // Sort range rewards by range_min (ascending)
    rangeRewards.sort((a, b) => a.value.range_min - b.value.range_min);

    return { positionRewards, rangeRewards };
  };

  const combineAllRewards = () => {
    const allRewards: Array<{
      position: number | string;
      rewards: Reward[];
      isRange?: boolean;
    }> = [];

    rewardsData.forEach((reward) => {
      if (reward.distribution_type === "position") {
        allRewards.push({
          position: reward.value,
          rewards: reward.rewards,
          isRange: false,
        });
      } else if (reward.distribution_type === "range") {
        const positionText =
          reward.value.range_min === reward.value.range_max
            ? `Position ${reward.value.range_min}`
            : `Positions ${reward.value.range_min} - ${reward.value.range_max}`;

        allRewards.push({
          position: positionText,
          rewards: reward.rewards,
          isRange: true,
        });
      }
    });

    // Sort by position (numeric positions first, then ranges)
    allRewards.sort((a, b) => {
      if (typeof a.position === "number" && typeof b.position === "number") {
        return a.position - b.position;
      }
      if (typeof a.position === "number") return -1;
      if (typeof b.position === "number") return 1;
      return 0;
    });

    return allRewards;
  };

  // Helper function to get icon for reward type
  const getRewardTypeIcon = (type: string) => {
    switch (type) {
      case "points":
        return "🎯";
      case "voucher":
        return "🎫";
      case "coupon":
        return "🏷️";
      default:
        return "🎁";
    }
  };

  const getRewardTypeDisplay = (type: string) => {
    switch (type) {
      case "points":
        return "Points";
      case "voucher":
        return "Voucher";
      case "coupon":
        return "Coupon";
      default:
        return type.charAt(0).toUpperCase() + type.slice(1);
    }
  };

  // Helper function to format currency
  const formatCurrency = (amount: number, currency?: string) => {
    if (!currency || currency === "INR") {
      return `₹${amount.toLocaleString()}`;
    } else if (currency === "USD") {
      return `$${amount.toLocaleString()}`;
    }
    return `${amount.toLocaleString()} ${currency}`;
  };

  // Helper function to format date
  const formatDate = (dateString?: string) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  if (!challenge) {
    return (
      <DashboardContainer>
        <LoadingSpinner>No challenge data available</LoadingSpinner>
      </DashboardContainer>
    );
  }

  // Get displayed leaderboard entries
  const displayedLeaderboard = leaderboard;

  return (
    <DashboardContainer>
      <RowContainer>
        <MainContent>
          <ChallengeCard>
            <ChallengeImage
              backgroundImage={
                challenge.image && challenge.image.trim() !== ""
                  ? `url(${challenge.image})` //  Remove the quotes around the URL
                  : "linear-gradient(135deg, #667eea, #764ba2)"
              }
            >
              {/* Top overlays - Goal and Members */}
              <TopOverlays>
                <GoalBadgeTopLeft>
                  <GoalIcon>{ChallangeIcon(challenge.metric_type)}</GoalIcon>
                  <div>
                    <div>{getGoalText(challenge)}</div>
                    <div style={{ fontSize: "12px", opacity: 0.9 }}>
                      {/* Pass the evaluation mode as second parameter */}
                      {ChallengeText(
                        challenge.metric_type,
                        challenge.goal_evaluation_mode,
                      )}
                    </div>
                  </div>
                </GoalBadgeTopLeft>

                <MembersBadge>
                  <MembersIcon>👥</MembersIcon>
                  {challenge.members_joined}
                </MembersBadge>
              </TopOverlays>

              {/* Bottom overlays - Visibility, Status, Challenge Name, and Date Range */}
              <BottomOverlay>
                <VisibilityBadge visibility={challenge.visibility}>
                  {challenge.visibility}
                </VisibilityBadge>
                <ChallengeDetails>
                  <StatusBadge status={getChallengeStatus(challenge)}>
                    {getChallengeStatus(challenge) === "yet to start"
                      ? "Yet to Start"
                      : getChallengeStatus(challenge)}
                  </StatusBadge>
                  <ChallengeName>{challenge.challenge_name}</ChallengeName>
                  <ChallengeDateRange>
                    {formatDateRange(
                      getChallengeStartDate(challenge),
                      getChallengeEndDate(challenge),
                    )}
                  </ChallengeDateRange>

                  {/* Only show Quit/Join buttons if challenge is not ended */}
                  {getChallengeStatus(challenge) !== "ended" && (
                    <>
                      {isUserJoined() ? (
                        <QuitButton
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowQuitModal(true);
                          }}
                        >
                          Quit Challenge
                        </QuitButton>
                      ) : challenge.visibility === "public" &&
                        challenge.challenge_type === "individual" ? (
                        <JoinButton
                          onClick={(e) => {
                            e.stopPropagation();
                            handleJoinChallenge(challenge);
                          }}
                        >
                          Join Challenge
                        </JoinButton>
                      ) : null}
                    </>
                  )}
                </ChallengeDetails>
              </BottomOverlay>
            </ChallengeImage>
            <ChallengeInfo>
              <ChallengeMeta>
                <DurationInfo>
                  <InfoLabel>Duration</InfoLabel>
                  <InfoValue>
                    {durationdays || 0} {durationdays === 1 ? "day" : "days"}
                  </InfoValue>
                </DurationInfo>

                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    flexWrap: "wrap",
                    flex: 1,
                    justifyContent:
                      challenge?.challenge_status === "active"
                        ? "center"
                        : "flex-end",
                  }}
                >
                  <ChallengeTypeInfo
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems:
                        challenge?.challenge_status === "active"
                          ? "center"
                          : "flex-end",
                      textAlign:
                        challenge?.challenge_status === "active"
                          ? "center"
                          : "right",
                    }}
                  >
                    <InfoLabel>Challenge Type</InfoLabel>
                    <InfoValue>
                      <span>
                        {challenge.metric_type === "steps" && "Steps"}
                        {challenge.metric_type === "distance" && "Distance"}
                        {challenge.metric_type === "caloriesburned" &&
                          "Calories Burned"}
                      </span>
                    </InfoValue>
                  </ChallengeTypeInfo>
                </div>

                {challenge?.challenge_status === "active" && isUserJoined() && (
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <ChatButton onClick={() => setShowChats(true)}>
                      <ChatIcon />
                      View Chats
                    </ChatButton>
                  </div>
                )}
              </ChallengeMeta>

              <AboutSection>
                <AboutTitle>About Challenge</AboutTitle>
                <AboutText>
                  {challenge.challenge_desc || "No description available"}
                </AboutText>
              </AboutSection>
            </ChallengeInfo>
          </ChallengeCard>

          {/* Rewards Section - Only show if challenge has rewards */}
        </MainContent>

        <Sidebar>
          <LeaderboardCard>
            <LeaderboardHeader>
              <TrophyIcon>🏆</TrophyIcon>
              <LeaderboardTitle>Leaderboard</LeaderboardTitle>
            </LeaderboardHeader>

            {loading ? (
              <LoadingSpinner>Loading leaderboard...</LoadingSpinner>
            ) : displayedLeaderboard.length > 0 ? (
              <>
                <LeaderboardList
                  hasCurrentUser={
                    isUserJoined() &&
                    challenge?.challenge_type === "individual" &&
                    // Check if user is ranked beyond top 5
                    (() => {
                      const currentUserEntry = leaderboard.find(
                        (entry) => entry.user_uid === userUid,
                      );
                      return currentUserEntry && currentUserEntry.rank > 5;
                    })()
                  }
                >
                  {displayedLeaderboard.map((entry) => {
                    // Get the image for individual users
                    const imageUid = entry.icon_image;
                    const imageBase64 = imageUid
                      ? userImages[imageUid]
                      : undefined;
                    const imageSrc = imageBase64
                      ? `data:image/png;base64,${imageBase64}`
                      : undefined;

                    return (
                      <LeaderboardItem
                        key={entry.id}
                        clickable={true}
                        isTeam={entry.type === "team"}
                        onClick={() => handleUserClick(entry)}
                      >
                        <RankBadge rank={entry.rank}>{entry.rank}</RankBadge>
                        <UserAvatar
                          image={imageSrc}
                          isTeam={entry.type === "team"}
                        >
                          {entry.type === "team" && "👥"}{" "}
                          {/* Only show team icon, no initials for users */}
                        </UserAvatar>
                        <UserInfo>
                          <UserName isTeam={entry.type === "team"}>
                            {entry.name}
                          </UserName>
                          <UserStats isTeam={entry.type === "team"}>
                            <span>
                              {entry.metrics.toLocaleString()}{" "}
                              {challenge?.metric_type === "distance"
                                ? "meters"
                                : challenge?.metric_type === "caloriesburned"
                                  ? "kcal"
                                  : challenge?.metric_type}
                            </span>
                            {entry.type === "team" && entry.member_count && (
                              <TeamMemberCount>
                                {entry.member_count} members
                              </TeamMemberCount>
                            )}
                          </UserStats>
                        </UserInfo>
                      </LeaderboardItem>
                    );
                  })}
                </LeaderboardList>

                {/* Only show "Your Position" section if user has joined the challenge AND rank is more than 5 */}
                {isUserJoined() &&
                  challenge?.challenge_type === "individual" &&
                  (() => {
                    // Find current user in the FULL leaderboard
                    const currentUserEntry = leaderboard.find(
                      (entry) => entry.user_uid === userUid,
                    );

                    // Only show if user is found AND rank is greater than 5
                    if (currentUserEntry && currentUserEntry.rank > 5) {
                      const imageUid = currentUserEntry.icon_image;
                      const imageBase64 = imageUid
                        ? userImages[imageUid]
                        : undefined;
                      const imageSrc = imageBase64
                        ? `data:image/png;base64,${imageBase64}`
                        : undefined;

                      return (
                        <CurrentUserPosition>
                          <CurrentUserItem
                            isCurrentUser={true}
                            onClick={() => handleUserClick(currentUserEntry)}
                          >
                            <RankBadge rank={currentUserEntry.rank}>
                              {currentUserEntry.rank}
                            </RankBadge>
                            <UserAvatar
                              image={imageSrc}
                              isTeam={currentUserEntry.type === "team"}
                            >
                              {currentUserEntry.type === "team" && "👥"}{" "}
                              {/* Only show team icon, no initials for users */}
                            </UserAvatar>
                            <UserInfo>
                              <UserName
                                isTeam={currentUserEntry.type === "team"}
                              >
                                {currentUserEntry.name}
                                <CurrentUserBadge>You</CurrentUserBadge>
                              </UserName>
                              <UserStats
                                isTeam={currentUserEntry.type === "team"}
                              >
                                <span>
                                  {currentUserEntry.metrics.toLocaleString()}{" "}
                                  {challenge?.metric_type === "distance"
                                    ? "meters"
                                    : challenge?.metric_type}
                                </span>
                                {currentUserEntry.type === "team" &&
                                  currentUserEntry.member_count && (
                                    <TeamMemberCount>
                                      {currentUserEntry.member_count} members
                                    </TeamMemberCount>
                                  )}
                              </UserStats>
                            </UserInfo>
                          </CurrentUserItem>
                        </CurrentUserPosition>
                      );
                    }

                    // If user is joined but not found in leaderboard, or rank is 5 or less, don't show anything
                    return null;
                  })()}
              </>
            ) : (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "40px 20px",
                  color: "#6b7280",
                  fontSize: "14px",
                  textAlign: "center",
                  flex: 1,
                }}
              >
                No users in the challenge
              </div>
            )}
          </LeaderboardCard>
        </Sidebar>
      </RowContainer>
      <FullWidthSection>
        {/* Rewards Section - Only show if challenge has rewards */}
        {hasRewards && (
          <RewardsCard>
            <RewardsHeader>
              <RewardIcon>🏆</RewardIcon>
              <RewardsTitle>Rewards</RewardsTitle>
            </RewardsHeader>

            {loadingRewards ? (
              <LoadingSpinnerSmall>Loading rewards...</LoadingSpinnerSmall>
            ) : rewardsData.length > 0 || participationPoints !== null ? (
              <SimplifiedRewardsContainer>
                {/* Combined Rewards List */}
                {combineAllRewards().map((rewardGroup, index) => (
                  <SimplifiedRewardItem key={`reward-${index}`}>
                    <PositionHeader>
                      {typeof rewardGroup.position === "number" ? (
                        <SimplifiedPositionBadge>
                          {rewardGroup.position}
                        </SimplifiedPositionBadge>
                      ) : (
                        <div
                          style={{
                            width: "32px",
                            height: "32px",
                            borderRadius: "50%",
                            background:
                              "linear-gradient(135deg, #3b82f6, #1d4ed8)",
                            color: "white",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontWeight: 700,
                            fontSize: "14px",
                          }}
                        >
                          {rewardGroup.isRange ? "🎯" : "🏅"}
                        </div>
                      )}
                      <PositionTitle isRange={rewardGroup.isRange}>
                        {typeof rewardGroup.position === "number"
                          ? `Position ${rewardGroup.position}`
                          : rewardGroup.position}
                      </PositionTitle>
                    </PositionHeader>

                    <SimplifiedRewardContent>
                      <SimplifiedRewardList>
                        {rewardGroup.rewards.map((reward, rIndex) => (
                          <SimplifiedRewardBadge
                            key={`reward-item-${rIndex}`}
                            type={reward.reward_type}
                          >
                            <SimplifiedRewardIcon>
                              {getRewardTypeIcon(reward.reward_type)}
                            </SimplifiedRewardIcon>
                            <RewardText>
                              {reward.reward_type === "points"
                                ? `${reward.reward_amount} points`
                                : reward.reward_name
                                  ? `${
                                      reward.reward_name
                                    } (${getRewardTypeDisplay(
                                      reward.reward_type,
                                    )})`
                                  : getRewardTypeDisplay(reward.reward_type)}

                              {/* Amount and currency on new line if needed */}
                              {reward.reward_type !== "points" && (
                                <div
                                  style={{
                                    fontSize: "11px",
                                    marginTop: "2px",
                                    color: "#6b7280",
                                  }}
                                >
                                  {formatCurrency(
                                    reward.reward_amount,
                                    reward.currency,
                                  )}
                                </div>
                              )}

                              {/* Platform on new line */}
                              {reward.platform && (
                                <div
                                  style={{
                                    fontSize: "10px",
                                    marginTop: "2px",
                                    color: "#9ca3af",
                                  }}
                                >
                                  {reward.platform}
                                </div>
                              )}
                            </RewardText>
                          </SimplifiedRewardBadge>
                        ))}
                      </SimplifiedRewardList>
                    </SimplifiedRewardContent>
                  </SimplifiedRewardItem>
                ))}

                {/* Participation Rewards - only if exists */}
                {participationPoints !== null && participationPoints > 0 && (
                  <SimplifiedParticipationRewards>
                    <SimplifiedParticipationHeader>
                      <SimplifiedParticipationIcon>
                        🎯
                      </SimplifiedParticipationIcon>
                      <SimplifiedParticipationTitle>
                        Participation Rewards
                      </SimplifiedParticipationTitle>
                    </SimplifiedParticipationHeader>
                    <SimplifiedParticipationPoints>
                      <SimplifiedParticipationBadge>
                        <span>🎯</span>
                        <span>{participationPoints} points</span>
                      </SimplifiedParticipationBadge>
                      <div
                        style={{
                          fontSize: "12px",
                          color: "#166534",
                          opacity: 0.8,
                          marginTop: "8px",
                        }}
                      >
                        Earn these points just for participating!
                      </div>
                    </SimplifiedParticipationPoints>
                  </SimplifiedParticipationRewards>
                )}
              </SimplifiedRewardsContainer>
            ) : (
              <NoRewards>No rewards assigned for this challenge</NoRewards>
            )}
          </RewardsCard>
        )}
      </FullWidthSection>
      {showUserModal && selectedUser && (
        <UserTimelineModal
          user={selectedUser}
          metricType={challenge.metric_type}
          onClose={() => setShowUserModal(false)}
        />
      )}
      {showQuitModal && (
        <ModalOverlay onClick={() => !isQuitting && setShowQuitModal(false)}>
          <QuitModalContainer onClick={(e) => e.stopPropagation()}>
            <QuitModalHeader>
              <WarningIcon>⚠️</WarningIcon>
              <QuitModalTitle>Quit Challenge</QuitModalTitle>
            </QuitModalHeader>

            <QuitModalMessage>
              Are you sure you want to quit{" "}
              <strong>"{challenge?.challenge_name}"</strong>? You will lose your
              current progress and position on the leaderboard.
            </QuitModalMessage>

            <div>
              <QuitReasonLabel>
                Reason for quitting <span style={{ color: "#dc3545" }}>*</span>
              </QuitReasonLabel>
              <QuitReasonTextarea
                placeholder="Please let us know why you're quitting this challenge..."
                value={quitReason}
                onChange={(e) => setQuitReason(e.target.value)}
                disabled={isQuitting}
                maxLength={500}
              />
              <div
                style={{
                  fontSize: "0.75rem",
                  color: "#9ca3af",
                  marginTop: "4px",
                  textAlign: "right",
                }}
              >
                {quitReason.length}/500
              </div>
            </div>

            <QuitModalActions>
              <CancelButton
                onClick={() => {
                  setShowQuitModal(false);
                  setQuitReason("");
                }}
                disabled={isQuitting}
              >
                Cancel
              </CancelButton>
              <ConfirmQuitButton
                onClick={handleQuitChallenge}
                disabled={!quitReason.trim() || isQuitting}
              >
                {isQuitting ? (
                  <>
                    <span>Quitting...</span>
                  </>
                ) : (
                  "Yes, Quit Challenge"
                )}
              </ConfirmQuitButton>
            </QuitModalActions>
          </QuitModalContainer>
        </ModalOverlay>
      )}
      {showChats && (
        <ViewChats
          challengeUid={challenge.challenge_uid}
          orgUid={challenge.org_uid}
          onClose={() => setShowChats(false)}
        />
      )}
    </DashboardContainer>
  );
};
export default ChallengeDashboard;
